// src/hooks/useContactHandler.js
import {useDispatch} from 'react-redux';
import {
    contactClear,
    setSelectedContact
} from '../actions/contacts/contacts';
import {messageClearSelectContactMessages} from '../actions/messages';
import {
    addContact,
    moveContact,
    removeContact,
    updateContact,
    markContact
} from "../actions/assignedContacts";
import {selectIdAgent} from "../actions/agentes";
import {showContactAssignedSnackbar} from "../actions/snackbar";
import {playContactAssignedSound} from "../notifications/notificationsPlayer";
import {getNotificationPreferences} from "../notifications/notificationSettings";

export const useContactHandler = (
    {
        refContactDetails,
        refAssignedAgentContacts,
        refIdMyAgent
    }
) => {
    const dispatch = useDispatch();

    const handleMarkContact = (prevContact, newContact, agent) => {
        if (!refContactDetails.current || !refContactDetails.current._id) return;
        if (!refIdMyAgent.current || !refIdMyAgent.current._id) return;
        // Si el contacto actual no tiene la propiedad 'hasMark', se retorna
        if (!('hasMark' in prevContact)) return;
        // Si el contacto actual no tiene marca, se retorna
        if (!prevContact.hasMark) return;

        // Si el contacto actualizado no es el mismo que el contacto en los detalles de contacto actuales
        // y el contacto actual tiene una marca, se elimina la marca utilizando la acción markContact
        if (prevContact._id !== refContactDetails.current._id && prevContact.hasMark) {
            dispatch(markContact({
                contact: prevContact,
                hasMark: false
            }));
            return;
        }

        // Si el agente asignado al contacto actualizado es el mismo que el agente actual,
        // se selecciona el contacto utilizando setSelectedContact y selectIdAgent
        if (newContact.id_agent === refIdMyAgent.current) {
            dispatch(setSelectedContact(newContact));
            dispatch(selectIdAgent(agent._id));
        }

        // Se elimina la marca del contacto utilizando la acción markContact
        dispatch(markContact({
            contact: prevContact,
            hasMark: false
        }));
    };

    const handleMoveAndLoadAgent = (oldContact, newContact) => {
        // Lógica opcional si necesitas ejecutar side effects adicionales
        // (placeholder para expandir funcionalidad más adelante)
        // Si el contacto actual tenía una marca, se elimina la marca utilizando la acción markContact
        if (oldContact.hasMark) {
            dispatch(markContact({
                contact: oldContact,
                hasMark: false
            }));
        }

        // Si el agente asignado al contacto actualizado es el mismo que el agente actual, se selecciona el contacto utilizando setSelectedContact y selectIdAgent
        if (newContact.id_agent === refIdMyAgent.current) {
            dispatch(setSelectedContact(newContact));
            dispatch(selectIdAgent(refIdMyAgent.current));
            return;
        }
    };

    /**
     * Función para manejar el movimiento de agente para un contacto
     * @param {object} contact - El contacto al que se aplica el movimiento de agente
     * @param {object} contactsAssigneds - El contacto al que se aplica el movimiento de agente
     */
    const handleAgentMove = (contact, contactsAssigneds) => {
        if (!refAssignedAgentContacts.current || refAssignedAgentContacts.current.length === 0) return;
        let agent = undefined;
        if (contactsAssigneds.length === 1) {
            agent = contactsAssigneds[0]
        } else {
            agent = contactsAssigneds.find(agentContact => agentContact._id === contact.id_agent);
        }

        const contacts = contactsAssigneds.flatMap((agentContact) => agentContact.contacts);

        let inListContact = contacts.find((agentContact) => agentContact._id === contact._id);


        if (!agent && !inListContact) {
            return;
        }

        if (agent && !inListContact) {
            // Contacto recién asignado a un agente que aún no estaba en ninguna lista
            // (p. ej. tomado desde una bandeja): conservar su id_agent, no forzarlo a null.
            const newContact = {...contact, hasMark: true};
            dispatch(addContact(newContact));
            inListContact = newContact;

        }

        if (inListContact.id_agent === contact.id_agent && agent) {
            dispatch(updateContact(contact));
            handleMarkContact(inListContact, contact, agent);
            return
        }

        if (inListContact.id_agent === null && inListContact.id_agent !== contact.id_agent && !agent) {
            dispatch(removeContact(inListContact));
        }

        if (inListContact.id_agent !== contact.id_agent && agent) {
            dispatch(moveContact({
                id_contact: contact._id,
                id_agent_source: inListContact.id_agent,
                id_agent_target: contact.id_agent,
            }));
            handleMarkContact(inListContact, contact, agent);
        }
        dispatch(updateContact(contact));

        handleMoveAndLoadAgent(inListContact, contact);
    };
    const updateContactReceived = (rawContact) => {
        // El servidor omite id_agent cuando es null → normalizarlo antes de cualquier comparación
        const contact = rawContact.id_agent !== undefined
            ? rawContact
            : { ...rawContact, id_agent: null };

        if (!refAssignedAgentContacts.current || refAssignedAgentContacts.current.length === 0) {
            return;
        }
        // Combina todos los contactos asignados en una sola lista
        let contacts = refAssignedAgentContacts.current.reduce((prevAgent, currentAgent) => {
            if (!Array.isArray(prevAgent)) {
                return prevAgent.contacts.concat(currentAgent.contacts);
            }
            return prevAgent.concat(currentAgent.contacts);
        });

        // Si solo hay un agente asignado, se utiliza su lista de contactos
        if (refAssignedAgentContacts.current.length === 1)
            contacts = refAssignedAgentContacts.current[0].contacts;

        // Encuentra el contacto local en la lista de contactos
        const localContact = contacts.find(contactEval => contactEval._id === contact._id);

        // Encuentra el agente del contacto recibido
        const contactAgent = refAssignedAgentContacts.current.find(agentContact => agentContact._id === contact.id_agent);

        console.log("🔍 updateContactReceived", {
            contact_id_agent: contact.id_agent,
            contactAgent: contactAgent?._id ?? null,
            assignedAgentIds: JSON.stringify(refAssignedAgentContacts.current.map(a => a._id)),
        });

        const isMyContact = contact.id_agent === refIdMyAgent.current;

        // El contacto se asigna al agente actual o a otro agente visible →
        // mover al inicio de la lista sin cambiar la conversación activa
        if (isMyContact || contactAgent) {
            const isNewAssignmentToMe = isMyContact && (!localContact || localContact.id_agent !== contact.id_agent);

            if (localContact && localContact.id_agent !== contact.id_agent) {
                dispatch(moveContact({
                    id_contact: contact._id,
                    id_agent_source: localContact.id_agent,
                    id_agent_target: contact.id_agent,
                }));
            } else if (localContact) {
                dispatch(updateContact(contact));
            } else {
                dispatch(addContact(contact));
            }

            if (isNewAssignmentToMe) {
                dispatch(showContactAssignedSnackbar(contact));
                const prefs = getNotificationPreferences();
                if (prefs.enabled) {
                    playContactAssignedSound(prefs.volume).catch(() => {});
                }
            }

            return;
        }

        // El contacto se asigna a un agente fuera de la vista actual → eliminar de la lista
        if (localContact && !contactAgent) {
            if (contact._id === refContactDetails.current?._id) {
                dispatch(contactClear());
                dispatch(messageClearSelectContactMessages());
            }
            dispatch(removeContact(contact));
        }
    }


    return {
        handleAgentMove,
        updateContactReceived
    };
};
